import CRYPTO_CURRENCY_CODE from './cryptoList.ts';

const intlCurrencyFormatOptions = (
  params?: Intl.NumberFormatOptions,
): Intl.NumberFormatOptions => {
  if (params?.currency && CRYPTO_CURRENCY_CODE.has(params.currency)) {
    return {
      ...params,
      style: 'currency',
      minimumFractionDigits: 9,
      maximumFractionDigits: 9,
    };
  }

  return {
    style: 'currency',
    currencyDisplay: 'symbol',
    maximumFractionDigits: params?.maximumFractionDigits,
    minimumFractionDigits: params?.minimumFractionDigits,
    ...params,
  };
};

type GetNumberFormatterProps = {
  languageCode: string;
  currency: string | undefined;
  style: 'currency' | 'decimal';
} & (
  | {
      fractionDigits: number;
      maximumFractionDigits?: number;
      minimumFractionDigits?: number;
    }
  | {
      fractionDigits?: number;
      maximumFractionDigits: number;
      minimumFractionDigits: number;
    }
);

class IntlFormatter {
  private static instance: IntlFormatter;
  private numberFormatters: Map<string, Intl.NumberFormat> = new Map();
  private dateFormatters: Map<string, Intl.DateTimeFormat> = new Map();

  private constructor() {}

  public static getInstance(): IntlFormatter {
    if (!IntlFormatter.instance) {
      IntlFormatter.instance = new IntlFormatter();
    }

    return IntlFormatter.instance;
  }

  public getNumberFormatter({
    languageCode,
    currency,
    style,
    fractionDigits,
    minimumFractionDigits,
    maximumFractionDigits,
  }: GetNumberFormatterProps): Intl.NumberFormat {
    const cacheKey = `${languageCode}-${currency}-${style}-${fractionDigits}`;

    let formatter = this.numberFormatters.get(cacheKey);

    if (!formatter) {
      formatter = new Intl.NumberFormat(
        languageCode,
        intlCurrencyFormatOptions({
          currency,
          style,
          maximumFractionDigits: maximumFractionDigits ?? fractionDigits,
          minimumFractionDigits: minimumFractionDigits ?? fractionDigits,
        }),
      );

      this.numberFormatters.set(cacheKey, formatter);
    }

    return formatter;
  }

  public getDateFormatter(languageCode: string): Intl.DateTimeFormat {
    let formatter = this.dateFormatters.get(languageCode);

    if (!formatter) {
      formatter = new Intl.DateTimeFormat(languageCode);
      this.dateFormatters.set(languageCode, formatter);
    }

    return formatter;
  }
}

export default IntlFormatter;
